import { useEffect, type RefObject } from 'react';

/**
 * Closes a popover/menu on outside pointer-down or Escape. Only active while
 * `open` is true. `onDismiss` should be stable (wrap in useCallback).
 */
export function useDismissable(
  open: boolean,
  ref: RefObject<HTMLElement>,
  onDismiss: () => void,
): void {
  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        onDismiss();
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onDismiss();
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, ref, onDismiss]);
}
